import React, { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  ArrowLeft,
  ArrowUp,
  CalendarDays,
  Clock3,
  Link2,
  Mail,
  Share2,
  UserRound,
} from "lucide-react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import ApiURl from "../controllers/Api";

gsap.registerPlugin(ScrollTrigger);

const PAGE_BG = "var(--user-bg, #E8D9D5)";
const CARD_BG = "var(--user-bg, #E8D9D5)";
const MAROON = "var(--user-maroon, #3A1115)";
const GOLD = "var(--user-gold, #C89A3D)";
const GOLD_LIGHT = "var(--user-gold-soft, #E9D2A1)";
const TEXT = "var(--user-text, #4B3C39)";
const TEXT_SOFT = "var(--user-text-soft, #776966)";

const capitalizeFirstLetter = (value) => {
  const text = String(value || "").trim();
  if (!text) return "";
  return text.charAt(0).toUpperCase() + text.slice(1);
};

export default function BlogDetail() {
  const { id } = useParams();

  const pageRef = useRef(null);
  const articleRef = useRef(null);
  const imageWrapRef = useRef(null);
  const imageRef = useRef(null);
  const introRef = useRef(null);
  const bodyRef = useRef(null);
  const shareRef = useRef(null);

  const [blog, setBlog] = useState(null);
  const [loading, setLoading] = useState(true);
  const [shareMessage, setShareMessage] = useState("");

  useEffect(() => {
    let mounted = true;

    setLoading(true);

    fetch(`${ApiURl}getBlogs.php?id=${encodeURIComponent(id)}&active_only=1`)
      .then((response) => response.json())
      .then((response) => {
        if (!mounted) return;

        setBlog(response?.success ? response.data : null);
      })
      .catch((error) => {
        console.error("Error loading blog:", error);

        if (mounted) {
          setBlog(null);
        }
      })
      .finally(() => {
        if (mounted) {
          setLoading(false);
        }
      });

    return () => {
      mounted = false;
    };
  }, [id]);

  useEffect(() => {
    if (loading || !blog) return undefined;

    const page = pageRef.current;
    const article = articleRef.current;
    const imageWrap = imageWrapRef.current;
    const image = imageRef.current;
    const intro = introRef.current;
    const body = bodyRef.current;
    const share = shareRef.current;

    if (!page || !article || !intro || !body) {
      return undefined;
    }

    const reduceMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)"
    ).matches;

    if (reduceMotion) {
      gsap.set(
        [
          article,
          imageWrap,
          image,
          intro,
          body.children,
          share,
        ],
        {
          x: 0,
          y: 0,
          scale: 1,
          autoAlpha: 1,
          clipPath: "inset(0% 0% 0% 0%)",
          clearProps: "transform",
        }
      );

      return undefined;
    }

    const context = gsap.context(() => {
      /*
       * Initial page entrance
       */
      const entranceTimeline = gsap.timeline({
        defaults: {
          ease: "power3.out",
        },
      });

      entranceTimeline
        .fromTo(
          article,
          {
            y: 35,
            autoAlpha: 0,
          },
          {
            y: 0,
            autoAlpha: 1,
            duration: 0.65,
          }
        )
        .fromTo(
          imageWrap,
          {
            y: 35,
            scale: 0.97,
            autoAlpha: 0,
            clipPath: "inset(8% 5% 8% 5% round 24px)",
          },
          {
            y: 0,
            scale: 1,
            autoAlpha: 1,
            clipPath: "inset(0% 0% 0% 0% round 22px)",
            duration: 0.85,
          },
          "-=0.35"
        )
        .fromTo(
          image,
          {
            scale: 1.1,
          },
          {
            scale: 1,
            duration: 1.15,
            ease: "power2.out",
          },
          "-=0.85"
        )
        .fromTo(
          intro,
          {
            y: 28,
            autoAlpha: 0,
          },
          {
            y: 0,
            autoAlpha: 1,
            duration: 0.7,
          },
          "-=0.65"
        );

      /*
       * Paragraph animation on scroll
       */
      const articleBlocks = gsap.utils.toArray(
        body.querySelectorAll("[data-blog-block]")
      );

      articleBlocks.forEach((block) => {
        gsap.fromTo(
          block,
          {
            y: 26,
            autoAlpha: 0,
          },
          {
            y: 0,
            autoAlpha: 1,
            duration: 0.7,
            ease: "power3.out",
            scrollTrigger: {
              trigger: block,
              start: "top 88%",
              toggleActions: "play none none reverse",
              invalidateOnRefresh: true,
            },
          }
        );
      });

      if (share) {
        gsap.fromTo(
          share,
          {
            y: 30,
            autoAlpha: 0,
          },
          {
            y: 0,
            autoAlpha: 1,
            duration: 0.7,
            ease: "power3.out",
            scrollTrigger: {
              trigger: share,
              start: "top 90%",
              toggleActions: "play none none reverse",
              invalidateOnRefresh: true,
            },
          }
        );
      }

      /*
       * Subtle image parallax
       */
      if (image) {
        gsap.fromTo(
          image,
          {
            yPercent: -3,
          },
          {
            yPercent: 3,
            ease: "none",
            scrollTrigger: {
              trigger: imageWrap,
              start: "top bottom",
              end: "bottom top",
              scrub: 1,
              invalidateOnRefresh: true,
            },
          }
        );
      }

      const refreshTimer = window.setTimeout(() => {
        ScrollTrigger.refresh();
      }, 250);

      const handleWindowLoad = () => {
        ScrollTrigger.refresh();
      };

      window.addEventListener("load", handleWindowLoad);

      return () => {
        window.clearTimeout(refreshTimer);
        window.removeEventListener("load", handleWindowLoad);
      };
    }, page);

    return () => {
      context.revert();
    };
  }, [blog, loading]);

  if (loading) {
    return (
      <main
        className="flex min-h-screen items-center justify-center px-5 font-[kanit]"
        style={{
          backgroundColor: PAGE_BG,
          color: MAROON,
        }}
      >
        <div className="flex flex-col items-center">
          <div
            className="h-10 w-10 animate-spin rounded-full border-2 border-transparent"
            style={{
              borderTopColor: GOLD,
              borderRightColor: GOLD_LIGHT,
            }}
          />

          <p className="mt-4 text-sm tracking-[0.12em]">
            Loading blog...
          </p>
        </div>
      </main>
    );
  }

  if (!blog) {
    return (
      <main
        className="flex min-h-screen items-center justify-center px-5 py-20 text-center font-[kanit]"
        style={{
          backgroundColor: PAGE_BG,
          color: MAROON,
        }}
      >
        <div>
          <span
            className="text-[10px] font-semibold uppercase tracking-[0.24em]"
            style={{
              color: GOLD,
            }}
          >
            SL Gold Journal
          </span>

          <h1 className="mt-4 font-serif text-4xl sm:text-5xl">
            Blog not found
          </h1>

          <Link
            to="/blogs"
            className="mt-7 inline-flex items-center gap-2 text-sm font-semibold"
            style={{
              color: "#B48728",
            }}
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Blogs
          </Link>
        </div>
      </main>
    );
  }

  const contentBlocks = (() => {
    const normalizeBlock = (block) => {
      if (!block) return null;

      if (block.type === "heading") {
        const value = String(block.value || "").trim();
        return value ? { type: "heading", value } : null;
      }

      // Preferred backend structure:
      // { type: "paragraph", lines: ["line 1", "line 2"] }
      if (Array.isArray(block.lines) && block.lines.length > 0) {
        const lines = block.lines
          .map((line) => String(line || "").trim())
          .filter(Boolean);

        return lines.length
          ? {
              type: "paragraph",
              value: lines.join("\n"),
              lines,
            }
          : null;
      }

      // Compatibility with older content_json values.
      const rawValue = String(block.value || "")
        .replace(/\r\n/g, "\n")
        .replace(/\r/g, "\n");

      const lines = rawValue
        .split("\n")
        .map((line) => line.trim())
        .filter(Boolean);

      return lines.length
        ? {
            type: "paragraph",
            value: lines.join("\n"),
            lines,
          }
        : null;
    };

    if (Array.isArray(blog.content) && blog.content.length > 0) {
      return blog.content.map(normalizeBlock).filter(Boolean);
    }

    if (typeof blog.content_json === "string" && blog.content_json.trim()) {
      try {
        const parsed = JSON.parse(blog.content_json);

        if (Array.isArray(parsed)) {
          return parsed.map(normalizeBlock).filter(Boolean);
        }
      } catch (error) {
        console.error("Unable to parse blog content_json:", error);
      }
    }

    // Old blogs: each old paragraph is one continuous line.
    if (Array.isArray(blog.paragraphs) && blog.paragraphs.length > 0) {
      return blog.paragraphs
        .filter(Boolean)
        .map((paragraph) => {
          const value = String(paragraph)
            .replace(/\s+/g, " ")
            .trim();

          return {
            type: "paragraph",
            value,
            lines: [value],
          };
        });
    }

    return [];
  })();

  const formattedDate = blog.created_at
    ? new Date(blog.created_at).toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      })
    : "";

  const category =
    blog.category ||
    blog.category_name ||
    blog.blog_category ||
    "Design & Craftsmanship";
  const shortDescription = capitalizeFirstLetter(blog.short_description);
  const blogUrl = window.location.href;
  const shareText = blog.short_description || blog.title;

  const copyBlogLink = async () => {
    try {
      await navigator.clipboard.writeText(blogUrl);
      setShareMessage("Blog link copied.");
    } catch (error) {
      console.error("Unable to copy blog link:", error);
      setShareMessage("Please copy the link from your browser.");
    }
  };

  const shareBlog = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: blog.title,
          text: shareText,
          url: blogUrl,
        });
        setShareMessage("Share dialog opened.");
        return;
      } catch (error) {
        if (error?.name === "AbortError") return;
        console.error("Unable to share blog:", error);
      }
    }

    copyBlogLink();
  };

  const emailBlog = () => {
    const subject = encodeURIComponent(blog.title);
    const body = encodeURIComponent(`${shareText}\n\n${blogUrl}`);
    window.location.href = `mailto:?subject=${subject}&body=${body}`;
  };

  return (
    <main
      ref={pageRef}
      className="relative min-h-screen overflow-hidden font-[kanit]"
      style={{
        backgroundColor: PAGE_BG,
        color: TEXT,
      }}
    >
      {/* Background glow */}
      <div
        className="pointer-events-none fixed inset-0"
        style={{
          background: `
            radial-gradient(
              circle at 85% 8%,
              rgba(200,154,61,0.12),
              transparent 28%
            ),
            radial-gradient(
              circle at 8% 55%,
              rgba(58,17,21,0.045),
              transparent 32%
            )
          `,
        }}
      />

      <article
        ref={articleRef}
        className="
          relative
          mx-auto
          min-h-screen
          w-full
          max-w-[1040px]
          bg-[#FFFCF8]
          px-4
          pb-12
          pt-5

          sm:px-8
          sm:pb-16
          sm:pt-8

          lg:px-16
          lg:pb-20
          lg:pt-10
        "
        style={{
          backgroundColor: CARD_BG,
          boxShadow: "0 0 80px rgba(58,17,21,0.07)",
        }}
      >
        {/* Desktop side progress decoration */}
        <aside className="absolute left-3 top-[160px] hidden xl:block">
          <div className="flex flex-col items-center">
            <span
              className="h-16 w-px"
              style={{
                background:
                  "linear-gradient(to bottom, transparent, #C89A3D)",
              }}
            />

            <span
              className="mt-3 text-[8px] font-semibold tracking-[0.15em]"
              style={{
                color: TEXT_SOFT,
              }}
            >
              01
            </span>

            <span
              className="my-2 h-5 w-px"
              style={{
                backgroundColor: GOLD_LIGHT,
              }}
            />

            <span
              className="text-[8px] font-semibold tracking-[0.15em]"
              style={{
                color: TEXT_SOFT,
              }}
            >
              05
            </span>
          </div>
        </aside>

        {/* Top navigation */}
        <header className="mb-5 flex items-center justify-between sm:mb-7">
          <Link
            to="/blogs"
            className="group inline-flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.14em]"
            style={{
              color: MAROON,
            }}
          >
            <span
              className="flex h-9 w-9 items-center justify-center rounded-full border transition-transform duration-300 group-hover:-translate-x-1"
              style={{
                borderColor: "rgba(58,17,21,0.14)",
                backgroundColor: "#FFFFFF",
              }}
            >
              <ArrowLeft className="h-4 w-4" />
            </span>

            <span className="hidden sm:inline">Back to Blogs</span>
          </Link>

      

          <button
            type="button"
            aria-label="Share article"
            onClick={shareBlog}
            className="flex h-9 w-9 items-center justify-center rounded-full border bg-white transition-transform duration-300 hover:-translate-y-1"
            style={{
              borderColor: "rgba(58,17,21,0.14)",
              color: MAROON,
            }}
          >
            <Share2 className="h-4 w-4" />
          </button>
        </header>

        {/* Main blog image */}
        {blog.image_url && (
          <div
            ref={imageWrapRef}
            className="
              relative
              h-[270px]
              overflow-hidden
              rounded-[18px]

              sm:h-[420px]
              sm:rounded-[22px]

              lg:h-[520px]
            "
            style={{
              backgroundColor: "#E9DED5",
              boxShadow: "0 18px 45px rgba(58,17,21,0.12)",
            }}
          >
            <img
              ref={imageRef}
              src={blog.image_url}
              alt={blog.title}
              loading="eager"
              decoding="async"
              className="absolute -top-[3%] left-0 h-[106%] w-full object-cover"
            />

            <div
              className="pointer-events-none absolute inset-0"
              style={{
                background:
                  "linear-gradient(180deg, rgba(58,17,21,0.02), rgba(58,17,21,0.14))",
              }}
            />

            <div className="pointer-events-none absolute inset-[7px] rounded-[13px] border border-white/30 sm:inset-[10px] sm:rounded-[16px]" />
          </div>
        )}

        {/* Intro */}
        <section
          ref={introRef}
          className="mx-auto mt-7 max-w-[820px] sm:mt-10"
        >
          <p
            className="text-[9px] font-semibold uppercase tracking-[0.17em] sm:text-[10px]"
            style={{
              color: GOLD,
            }}
          >
            {category}
          </p>

          <h1
            className="
              mt-3
              max-w-[780px]
              font-serif
              text-[31px]
              font-medium
              leading-[1.02]
              tracking-[-0.035em]

              sm:text-[48px]
              sm:leading-[1.04]

              lg:text-[58px]
            "
            style={{
              color: MAROON,
            }}
          >
            {blog.title}
          </h1>

          {/* Metadata */}
          <div
            className="
              mt-5
              flex
              flex-wrap
              items-center
              gap-x-3
              gap-y-2
              text-[9px]

              sm:gap-x-5
              sm:text-[11px]
            "
            style={{
              color: TEXT_SOFT,
            }}
          >
            <span className="inline-flex items-center gap-1.5">
              <UserRound className="h-3 w-3 sm:h-3.5 sm:w-3.5" />
              By {blog.author || blog.author_name || "SL Gold"}
            </span>

            <span
              className="h-[3px] w-[3px] rounded-full"
              style={{
                backgroundColor: GOLD,
              }}
            />

            {formattedDate && (
              <>
                <span className="inline-flex items-center gap-1.5">
                  <CalendarDays className="h-3 w-3 sm:h-3.5 sm:w-3.5" />
                  {formattedDate}
                </span>

                <span
                  className="h-[3px] w-[3px] rounded-full"
                  style={{
                    backgroundColor: GOLD,
                  }}
                />
              </>
            )}

            {blog.read_time && (
              <span className="inline-flex items-center gap-1.5">
                <Clock3 className="h-3 w-3 sm:h-3.5 sm:w-3.5" />
                {blog.read_time}
              </span>
            )}
          </div>

          {/* Decorative divider */}
          <div className="mt-6 flex items-center sm:mt-8">
            <span
              className="h-px flex-1"
              style={{
                background:
                  "linear-gradient(to right, transparent, rgba(200,154,61,0.45))",
              }}
            />

            <span
              className="mx-3 block h-1.5 w-1.5 rotate-45"
              style={{
                backgroundColor: GOLD,
              }}
            />

            <span
              className="h-px flex-1"
              style={{
                background:
                  "linear-gradient(to left, transparent, rgba(200,154,61,0.45))",
              }}
            />
          </div>

          {shortDescription && (
            <p
              className="
                mt-6
                text-[14px]
                font-medium
                leading-[1.75]

                sm:mt-8
                sm:text-[17px]
                sm:leading-8
              "
              style={{
                color: "#5D4C48",
              }}
            >
              <span
                className="
                  float-left
                  mr-2
                  mt-1
                  font-serif
                  text-[52px]
                  font-medium
                  leading-[0.75]

                  sm:mr-3
                  sm:text-[72px]
                "
                style={{
                  color: GOLD,
                }}
              >
                {shortDescription.charAt(0)}
              </span>
              {shortDescription.slice(1)}
            </p>
          )}
        </section>

        {/* Article paragraphs */}
        <section
          ref={bodyRef}
          className="
            mx-auto
            mt-7
            max-w-[760px]
            text-[13px]
            leading-[1.85]

            sm:mt-10
            sm:text-[16px]
            sm:leading-8

            lg:text-[17px]
            lg:leading-9
          "
          style={{
            color: TEXT,
          }}
        >
          {contentBlocks.map((block, index) => {
            if (block.type === "heading") {
              return (
                <h2
                  key={`heading-${index}-${block.value.slice(0, 20)}`}
                  data-blog-block
                  className="
                    mb-2
                    mt-8
                    font-serif
                    text-[24px]
                    font-semibold
                    leading-[1.25]
                    tracking-[-0.02em]

                    first:mt-0

                    sm:mb-2.5
                    sm:mt-10
                    sm:text-[29px]
                    sm:leading-[1.3]

                    lg:text-[31px]
                  "
                  style={{
                    color: MAROON,
                  }}
                >
                  {block.value}
                </h2>
              );
            }

            return (
              <p
                key={`paragraph-${index}-${block.value.slice(0, 20)}`}
                data-blog-block
                className="
                  mb-6

                  sm:mb-8
                "
              >
                {(block.lines || [block.value]).map((line, lineIndex) => (
                  <React.Fragment key={`${lineIndex}-${line.slice(0, 20)}`}>
                    {line}
                    {lineIndex < (block.lines || [block.value]).length - 1 && <br />}
                  </React.Fragment>
                ))}
              </p>
            );
          })}
        </section>

        {/* Article end divider */}
        <div className="mx-auto mt-10 max-w-[820px] sm:mt-14">
          <div
            className="h-px w-full"
            style={{
              background:
                "linear-gradient(90deg, transparent, rgba(200,154,61,0.55), transparent)",
            }}
          />
        </div>

        {/* Share section */}
        <section
          ref={shareRef}
          className="mx-auto mt-6 max-w-[820px] text-center sm:mt-8"
        >
          <p
            className="text-[8px] font-semibold uppercase tracking-[0.2em] sm:text-[9px]"
            style={{
              color: GOLD,
            }}
          >
            Share this story
          </p>

          <div className="mt-4 flex items-center justify-center gap-3">
            <button
              type="button"
              aria-label="Share article"
              onClick={shareBlog}
              className="flex h-10 w-10 items-center justify-center rounded-full border bg-white transition-all duration-300 hover:-translate-y-1"
              style={{
                borderColor: "rgba(200,154,61,0.35)",
                color: MAROON,
              }}
            >
              <Share2 className="h-4 w-4" />
            </button>

            <button
              type="button"
              aria-label="Send article by email"
              onClick={emailBlog}
              className="flex h-10 w-10 items-center justify-center rounded-full border bg-white transition-all duration-300 hover:-translate-y-1"
              style={{
                borderColor: "rgba(200,154,61,0.35)",
                color: MAROON,
              }}
            >
              <Mail className="h-4 w-4" />
            </button>

            <button
              type="button"
              aria-label="Copy article link"
              onClick={copyBlogLink}
              className="flex h-10 w-10 items-center justify-center rounded-full border bg-white transition-all duration-300 hover:-translate-y-1"
              style={{
                borderColor: "rgba(200,154,61,0.35)",
                color: MAROON,
              }}
            >
              <Link2 className="h-4 w-4" />
            </button>
          </div>

          {shareMessage && (
            <p
              className="mt-3 text-[11px] font-medium"
              style={{
                color: TEXT_SOFT,
              }}
            >
              {shareMessage}
            </p>
          )}
        </section>

    
      </article>

      {/* Floating scroll-to-top button */}
      <button
        type="button"
        aria-label="Scroll to top"
        onClick={() => {
          window.scrollTo({
            top: 0,
            behavior: "smooth",
          });
        }}
        className="
          fixed
          bottom-5
          right-4
          z-30
          flex
          h-12
          w-12
          items-center
          justify-center
          rounded-full
          border
          bg-white
          shadow-[0_12px_30px_rgba(58,17,21,0.16)]
          transition-transform
          duration-300

          hover:-translate-y-1

          sm:bottom-8
          sm:right-8
          sm:h-14
          sm:w-14
        "
        style={{
          borderColor: "rgba(58,17,21,0.12)",
          color: MAROON,
        }}
      >
        <ArrowUp className="h-5 w-5" />
      </button>
    </main>
  );
}